import React, { useRef, useState } from "react";
import { toast } from "sonner";
import { Contact, Loader2, X } from "lucide-react";
import { api } from "../../lib/api";
import { toastApiError } from "../../lib/upgrade";

const canPick = () => typeof navigator !== "undefined" && "contacts" in navigator && "ContactsManager" in window;
const GROUPS = ["Family", "Friends", "Work"];

export function parseVcf(text) {
  const out = [];
  const unfolded = text.replace(/\r?\n[ \t]/g, "");
  for (const card of unfolded.split(/BEGIN:VCARD/i).slice(1)) {
    const get = (re) => (card.match(re) || [])[1]?.trim() || "";
    let name = get(/^FN[^:]*:(.*)$/im);
    if (!name) { const n = get(/^N[^:]*:(.*)$/im).split(";"); name = [n[1], n[0]].filter(Boolean).join(" "); }
    const email = get(/^EMAIL[^:]*:(.*)$/im);
    const phone = get(/^TEL[^:]*:(.*)$/im);
    if (name || email || phone) out.push({ name: name.replace(/\\,/g, ",").slice(0, 120), email: email.slice(0, 160), phone: phone.slice(0, 40) });
  }
  return out;
}

function Review({ rows, setRows, onSave, busy, onClose }) {
  const picked = rows.filter((r) => r.on).length;
  const setAll = (group) => group && setRows(rows.map((x) => (x.on ? { ...x, group } : x)));
  return (
    <div className="fixed inset-0 z-[80] bg-black/50 flex items-end sm:items-center justify-center p-0 sm:p-4" data-testid="contacts-review">
      <div className="bg-white w-full sm:max-w-lg rounded-t-2xl sm:rounded-2xl max-h-[85dvh] flex flex-col">
        <div className="p-5 border-b border-[var(--line)] flex items-center justify-between"><p className="font-serif text-2xl">Add {picked} guest{picked === 1 ? "" : "s"}</p><button onClick={onClose} aria-label="Close" data-testid="contacts-close"><X className="w-5 h-5" /></button></div>
        <div className="px-5 py-3 border-b border-[var(--line)] flex items-center gap-2 text-xs" data-testid="contacts-group-all">
          <span className="text-[var(--muted)]">Put everyone ticked in:</span>
          {GROUPS.map((g) => <button key={g} onClick={() => setAll(g)} className="px-3 py-1 rounded-full border border-[var(--line)] hover:border-[var(--gold)] transition-colors" data-testid={`contacts-all-${g.toLowerCase()}`}>{g}</button>)}
        </div>
        <div className="overflow-y-auto divide-y divide-[var(--line)]">
          {rows.map((r, i) => (
            <div key={i} className="flex items-center gap-3 px-5 py-3 text-sm" data-testid={`contact-row-${i}`}>
              <input type="checkbox" checked={r.on} onChange={(e) => setRows(rows.map((x, k) => (k === i ? { ...x, on: e.target.checked } : x)))} aria-label={`Add ${r.name}`} data-testid={`contact-check-${i}`} />
              <span className="flex-1 min-w-0"><span className="block truncate font-medium">{r.name || r.email || r.phone}</span><span className="block truncate text-xs text-[var(--muted)]">{[r.email, r.phone].filter(Boolean).join(" · ") || "No email or phone"}</span></span>
              {r.dupe ? <span className="text-[10px] uppercase tracking-wider text-[var(--muted)]">Already added</span> : (
                <select value={r.group} onChange={(e) => setRows(rows.map((x, k) => (k === i ? { ...x, group: e.target.value } : x)))} className="text-xs border border-[var(--line)] rounded-full px-2 py-1 bg-white" data-testid={`contact-group-${i}`}>
                  {GROUPS.map((g) => <option key={g}>{g}</option>)}
                </select>
              )}
            </div>
          ))}
        </div>
        <div className="p-5 border-t border-[var(--line)]"><button onClick={onSave} disabled={busy || !picked} className="btn-gold w-full justify-center" data-testid="contacts-save">{busy ? <Loader2 className="w-4 h-4 animate-spin" /> : null} Add to guest list</button></div>
      </div>
    </div>
  );
}

export function PhoneContactsImport({ guests, onSaved }) {
  const ref = useRef(null);
  const [rows, setRows] = useState(null);
  const [busy, setBusy] = useState(false);
  function review(list) {
    const seen = new Set(guests.flatMap((g) => [g.email?.toLowerCase(), (g.phone || "").replace(/\D/g, "")]).filter(Boolean));
    const r = list.map((c) => ({ ...c, dupe: seen.has(c.email.toLowerCase()) || (c.phone && seen.has(c.phone.replace(/\D/g, ""))) })).map((c) => ({ ...c, on: !c.dupe, group: "Family" }));
    if (!r.length) return toast.error("No contacts found");
    setRows(r);
  }
  async function start() {
    if (!canPick()) return ref.current?.click();
    try {
      const picked = await navigator.contacts.select(["name", "email", "tel"], { multiple: true });
      review(picked.map((c) => ({ name: (c.name?.[0] || "").slice(0, 120), email: (c.email?.[0] || "").slice(0, 160), phone: (c.tel?.[0] || "").slice(0, 40) })));
    } catch { ref.current?.click(); }
  }
  async function onFile(e) {
    const f = e.target.files?.[0];
    e.target.value = "";
    if (f) review(parseVcf(await f.text()));
  }
  async function save() {
    setBusy(true);
    try {
      const list = rows.filter((r) => r.on).map(({ name, email, phone, group }) => ({ name: name || email || phone, email, phone, role: group }));
      const { data } = await api.post("/import/commit", { kind: "guest", rows: list, add_to_guest_list: true });
      toast.success(`${data.guests_added} guest${data.guests_added === 1 ? "" : "s"} added from your contacts`);
      setRows(null); onSaved();
    } catch (err) { toastApiError(err); } finally { setBusy(false); }
  }
  return (
    <>
      <button onClick={start} className="btn-outline-gold" data-testid="guests-phone-import-btn" title={canPick() ? "Pick from your phone contacts" : "Import a contacts file (.vcf)"}><Contact className="w-4 h-4" /> From phone</button>
      <input ref={ref} type="file" accept=".vcf,text/vcard,text/x-vcard" hidden onChange={onFile} data-testid="guests-vcf-input" />
      {rows && <Review rows={rows} setRows={setRows} onSave={save} busy={busy} onClose={() => setRows(null)} />}
    </>
  );
}
